import { computed, inject, Injectable, Signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { BehaviorSubject, map, Observable, take } from "rxjs";
import { AuthService } from "../../../subscription/services/auth/auth.service";
import { SuggestionCategory, SuggestionItem } from "../suggestions/suggestion-data";
import { JaxStateService } from "./jax-state.service";
import { UserPromptsService } from "./user-prompts.service";

export type SuggestionsStatus = "loading" | "empty" | "ready";

export interface SuggestionRequest {
  message: string;
  openJax?: boolean; // Whether to automatically open Jax if it's closed
  focus?: boolean; // Whether to focus the input after populating
}

export interface UserContext {
  subjects: string[];
  grades: string[];
  state: string;
  bibleEnabled: boolean;
}

@Injectable({
  providedIn: "root",
})
export class SuggestionService {
  private authService = inject(AuthService);
  private userPromptsService = inject(UserPromptsService);
  private jaxState = inject(JaxStateService);
  private suggestionRequestSubject = new BehaviorSubject<
    SuggestionRequest | null
  >(null);
  public suggestionRequest$ = this.suggestionRequestSubject.asObservable();

  /**
   * User context derived from auth state - single source of truth
   */
  readonly userContext$: Observable<UserContext> = this.authService.$user.pipe(
    map((user) => ({
      subjects: user?.subject_areas || [],
      grades: user?.grade_levels || [],
      state: user?.teaching_state || "Oklahoma",
      bibleEnabled: user?.bible_enabled || false,
    }))
  );

  /**
   * Categories sourced from the user's saved prompt blob.
   * Empty array until the user has a generated blob — callers should check
   * `suggestionsStatus` to render an appropriate placeholder.
   */
  readonly suggestionCategoriesSig: Signal<SuggestionCategory[]> = computed(() => {
    const blob = this.userPromptsService.prompts.value()?.prompts;
    if (!blob?.categories?.length) return [];

    return blob.categories
      .filter((c) => c.enabled)
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map<SuggestionCategory>((c) => ({
        id: c.id,
        title: c.label,
        items: c.prompts
          .filter((p) => p.enabled)
          .slice()
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map<SuggestionItem>((p) => ({
            title: p.title,
            message: p.message,
            attachments: p.attachments && p.attachments.length > 0
              ? p.attachments
              : undefined,
          })),
      }))
      .filter((c) => c.items.length > 0);
  });

  readonly suggestionCategories$: Observable<SuggestionCategory[]> = toObservable(
    this.suggestionCategoriesSig,
  );

  /**
   * Render-state for the suggestions UI.
   * - loading: a fetch/seed call is in flight
   * - empty: blob loaded but has no enabled categories
   * - ready: categories available
   */
  readonly suggestionsStatus: Signal<SuggestionsStatus> = computed(() => {
    if (this.userPromptsService.prompts.isLoading()) return "loading";
    return this.suggestionCategoriesSig().length > 0 ? "ready" : "empty";
  });

  /**
   * Help description text based on user's Bible setting
   */
  readonly helpDescription$: Observable<string> = this.userContext$.pipe(
    map((ctx) => ctx.bibleEnabled
      ? "I can help you create lesson plans, find educational standards, link scripture to your lesson plans, manage your schedule, and navigate the app."
      : "I can help you create lesson plans, find educational standards, manage your schedule, and navigate the app."
    )
  );

  // Helper method to get current user synchronously when needed
  private getCurrentUser() {
    let currentUser: any = null;
    this.authService.$user.pipe(take(1)).subscribe((user) =>
      currentUser = user
    );
    return currentUser;
  }

  /**
   * Populate the Jax assistant input with a message
   * @param message The message to populate in the input
   * @param openJax Whether to automatically open Jax if it's closed (default: true)
   * @param focus Whether to focus the input after populating (default: true)
   */
  populateInput(
    message: string,
    openJax: boolean = true,
    focus: boolean = true,
  ): void {
    if (openJax && !this.jaxState.isOpen()) {
      this.jaxState.open();
    }
    this.suggestionRequestSubject.next({
      message,
      openJax,
      focus,
    });
  }

  /**
   * Create a lesson plan suggestion with context
   * @param subject The subject for the lesson
   * @param gradeLevel The grade level
   * @param topic Optional specific topic
   */
  createLessonPlan(subject: string, gradeLevel: string, topic?: string): void {
    const topicText = topic ? ` on ${topic}` : "";
    this.populateInput(
      `Create a lesson plan for ${gradeLevel} ${subject}${topicText}`,
    );
  }

  /**
   * Find standards suggestion with context
   * @param subject The subject
   * @param gradeLevel The grade level
   * @param state Optional state/authority (defaults to Oklahoma)
   */
  findStandards(
    subject: string,
    gradeLevel: string,
    state: string = "Oklahoma",
  ): void {
    this.populateInput(`Find ${state} ${subject} standards for ${gradeLevel}`);
  }

  /**
   * Look up a Bible verse (only if Bible is enabled)
   * @param reference The verse reference (e.g., "John 3:16")
   */
  lookupVerse(reference: string): void {
    const user = this.getCurrentUser();
    if (user?.bible_enabled) {
      this.populateInput(`Go to ${reference}`);
    }
  }

  /**
   * Navigate to a specific section
   * @param section The section to navigate to
   */
  navigateTo(section: string): void {
    this.populateInput(`Take me to ${section}`);
  }

  /**
   * Ask about today's plans
   */
  showTodaysPlans(): void {
    this.populateInput("Show me what's planned for today");
  }

  /**
   * Ask about this week's plans
   */
  showWeeksPlans(): void {
    this.populateInput("Show me my plans for this week");
  }

  /**
   * Generic help request
   * @param context Optional context for the help request
   */
  askForHelp(context?: string): void {
    const message = context
      ? `Help me with ${context}`
      : "What can you help me with?";
    this.populateInput(message);
  }

  /**
   * Search for standards by topic/content
   * @param topic The topic or content area to search for
   */
  searchStandardsByTopic(topic: string): void {
    this.populateInput(`Find standards about ${topic}`);
  }

  /**
   * Create a worksheet or activity
   * @param type The type of content (worksheet, quiz, activity, etc.)
   * @param subject The subject area
   * @param gradeLevel The grade level
   * @param topic Optional specific topic
   */
  createContent(
    type: "worksheet" | "quiz" | "activity" | "handout" | "rubric",
    subject: string,
    gradeLevel: string,
    topic?: string,
  ): void {
    const topicText = topic ? ` about ${topic}` : "";
    this.populateInput(
      `Create a ${type} for ${gradeLevel} ${subject}${topicText}`,
    );
  }

  /**
   * View my documents/artifacts
   */
  viewMyDocuments(): void {
    this.populateInput("Show me my documents");
  }

  /**
   * Get planning summary
   */
  getPlanningOverview(): void {
    this.populateInput("Show me my planning overview");
  }

  /**
   * Create a new planner
   * @param subject The subject for the planner
   * @param theme Optional theme or description
   */
  createPlanner(subject: string, theme?: string): void {
    const themeText = theme ? ` with ${theme} theme` : "";
    this.populateInput(`Create a new planner for ${subject}${themeText}`);
  }

  /**
   * Find Bible verses by topic (only if Bible is enabled)
   * @param topic The topic or theme to search for
   */
  findBibleVerses(topic: string): void {
    const user = this.getCurrentUser();
    if (user?.bible_enabled) {
      this.populateInput(`Suggest Bible verses about ${topic}`);
    }
  }

  /**
   * Open Bible viewer (only if Bible is enabled)
   */
  openBible(): void {
    const user = this.getCurrentUser();
    if (user?.bible_enabled) {
      this.populateInput("Open the Bible");
    }
  }

  /**
   * Clear any pending suggestion request
   */
  clearRequest(): void {
    this.suggestionRequestSubject.next(null);
  }

  // ============= PROFILE-AWARE CONVENIENCE METHODS =============

  /**
   * Create lesson plan using user's default subjects/grades
   * @param topic Optional specific topic
   */
  createLessonPlanForMe(topic?: string): void {
    const user = this.getCurrentUser();
    if (user?.subject_areas?.[0] && user?.grade_levels?.[0]) {
      this.createLessonPlan(user.subject_areas[0], user.grade_levels[0], topic);
    } else {
      this.populateInput(
        `Create a lesson plan${topic ? ` about ${topic}` : ""}`,
      );
    }
  }

  /**
   * Find standards using user's profile (state, subject, grade)
   * @param topic Optional specific topic to search for
   */
  findStandardsForMe(topic?: string): void {
    const user = this.getCurrentUser();
    const state = user?.teaching_state || "Oklahoma";
    const subject = user?.subject_areas?.[0];
    const grade = user?.grade_levels?.[0];

    if (topic) {
      this.searchStandardsByTopic(topic);
    } else if (subject && grade) {
      this.findStandards(subject, grade, state);
    } else {
      this.populateInput(`Find ${state} standards`);
    }
  }

  /**
   * Create content using user's profile data
   * @param type The type of content to create
   * @param topic Optional specific topic
   */
  createContentForMe(
    type: "worksheet" | "quiz" | "activity" | "handout" | "rubric",
    topic?: string,
  ): void {
    const user = this.getCurrentUser();
    const subject = user?.subject_areas?.[0];
    const grade = user?.grade_levels?.[0];

    if (subject && grade) {
      this.createContent(type, subject, grade, topic);
    } else {
      const topicText = topic ? ` about ${topic}` : "";
      this.populateInput(`Create a ${type}${topicText}`);
    }
  }

  /**
   * Create planner using user's primary subject
   * @param theme Optional theme
   */
  createPlannerForMe(theme?: string): void {
    const user = this.getCurrentUser();
    const subject = user?.subject_areas?.[0];

    if (subject) {
      this.createPlanner(subject, theme);
    } else {
      const themeText = theme ? ` with ${theme} theme` : "";
      this.populateInput(`Create a new planner${themeText}`);
    }
  }

  /**
   * Quick access to create common content types for user's profile
   */
  quickCreateWorksheet(topic?: string): void {
    this.createContentForMe("worksheet", topic);
  }

  quickCreateQuiz(topic?: string): void {
    this.createContentForMe("quiz", topic);
  }

  quickCreateActivity(topic?: string): void {
    this.createContentForMe("activity", topic);
  }

  /**
   * Check if user has profile data for smart suggestions
   */
  get hasProfileData$(): Observable<boolean> {
    return this.userContext$.pipe(
      map((ctx) => !!(ctx.subjects.length && ctx.grades.length))
    );
  }

  /**
   * Check if Bible features are enabled for the user
   */
  get bibleEnabled$(): Observable<boolean> {
    return this.userContext$.pipe(map((ctx) => ctx.bibleEnabled));
  }
}
